import { useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Drawer, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useTenant } from "hooks/useTenant";

import { GET_SHIPPING_DOCUMENT_QUERY } from "../ShippingDocuments/api";
import { ShippingDocumentDetailContent } from "./ShippingDocumentDetailContent";

interface ShippingDocumentDrawerProps {
  shippingDocumentId: string | undefined;
  open: boolean;
  onClose: () => void;
}

/**
 * Bill-of-lading drawer opened from a `ManifestsV3` row click, mirroring
 * `ManifestDetails`'s manifest drawer: URL-driven (via
 * `?shippingDocumentId=`, see `shippingDocumentDrawerParam.ts`) so it's
 * linkable and survives reload. The body it renders lives in
 * `ShippingDocumentDetailContent`, which this drawer is the only consumer of.
 */
export function ShippingDocumentDrawer({
  shippingDocumentId,
  open,
  onClose,
}: ShippingDocumentDrawerProps) {
  const { tenantId } = useTenant();
  const { data, loading, error } = useQuery(GET_SHIPPING_DOCUMENT_QUERY, {
    // Gated on `open`, not only on the id. The shipment detail page supplies
    // the id from its own reconciliation on every load, so skipping on the id
    // alone fetched the whole document — header, facility, a freshly signed
    // download link, and every line's per-row authorization lookups — for a
    // drawer nobody opened. `ManifestDrawer` gates its body the same way.
    variables: { id: shippingDocumentId ?? "" },
    skip: !shippingDocumentId || !open,
  });
  const document = data?.wasteShippingDocument;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "90%", sm: 720, md: 960, lg: 1200, xl: 1400 },
          boxSizing: "border-box",
        },
      }}
    >
      {/*
        Only the states that render no header carry their own close button. Once
        there is a document, its header row owns Close alongside the other
        actions, which puts the title on the drawer's first line instead of a
        block below a row holding nothing else.
      */}
      {!(document && tenantId) && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 2 }}>
          <Tooltip title="Close">
            <IconButton aria-label="Close" size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {loading && !document && (
        <Stack spacing={2} sx={{ px: 3, py: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={48} />
          <Skeleton variant="rectangular" width="100%" height={200} />
          <Skeleton variant="rectangular" width="100%" height={200} />
        </Stack>
      )}
      {!loading && !tenantId && (
        <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
          <Typography variant="h6">No tenant selected</Typography>
          <Typography variant="body2" color="text.secondary">
            Select an organization to view this shipping document.
          </Typography>
        </Box>
      )}
      {!loading && tenantId && (error || !document) && (
        <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
          <Typography variant="h6">Shipping document not found</Typography>
          <Typography variant="body2" color="text.secondary">
            It may have been deleted, or the link may be incorrect.
          </Typography>
        </Box>
      )}
      {document && tenantId && (
        <ShippingDocumentDetailContent
          tenantId={tenantId}
          document={document}
          onDeleted={onClose}
          onClose={onClose}
        />
      )}
    </Drawer>
  );
}
